import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Heart } from "lucide-react";
import { saveAuthRedirect } from "@/lib/auth-redirect";
import { saveJob, unsaveJob } from "@/lib/saved-jobs";
import { track } from "@/lib/product-analytics";
import { cn } from "@/lib/utils";

interface SaveJobButtonProps {
  jobId: string;
  jobSlug?: string | null;
  /** "icon" = square icon button (mobile bar), "default" = icon + label */
  variant?: "icon" | "default";
  className?: string;
}

export default function SaveJobButton({
  jobId,
  jobSlug,
  variant = "default",
  className,
}: SaveJobButtonProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (cancelled || !session?.user) return;
      setUserId(session.user.id);
      const { data } = await supabase
        .from("saved_jobs")
        .select("id")
        .eq("user_id", session.user.id)
        .eq("job_id", jobId)
        .maybeSingle();
      if (!cancelled) setSaved(!!data);
    });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!userId) {
      saveAuthRedirect();
      toast({
        title: "Sign in to save shifts",
        description: "Your saved shifts appear on your dashboard.",
      });
      navigate("/auth");
      return;
    }
    if (busy) return;

    // Optimistic toggle — revert on failure.
    const next = !saved;
    setSaved(next);
    setBusy(true);
    try {
      if (next) {
        await saveJob(userId, jobId);
        track("job_saved", { jobSlug: jobSlug || jobId });
      } else {
        await unsaveJob(userId, jobId);
        track("job_unsaved", { jobSlug: jobSlug || jobId });
      }
    } catch (err) {
      setSaved(!next);
      toast({
        title: "Couldn't update saved shifts",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const heart = (
    <Heart
      className={cn(
        "h-4 w-4 transition-colors",
        saved ? "fill-red-500 text-red-500" : "text-muted-foreground"
      )}
    />
  );

  if (variant === "icon") {
    return (
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={handleClick}
        aria-label={saved ? "Remove from saved shifts" : "Save this shift"}
        aria-pressed={saved}
        className={cn("h-12 w-12 flex-shrink-0", className)}
      >
        {heart}
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      onClick={handleClick}
      aria-pressed={saved}
      className={cn("h-11 px-4 gap-2 text-base", className)}
    >
      {heart}
      {saved ? "Saved" : "Save"}
    </Button>
  );
}
